import React, { useEffect, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import { PageHeader, Panel, Button, Badge } from "../components/UIKit";
import { Save, Plus, Trash2, Settings2, FileLock2, Mail, Send } from "lucide-react";

const CONFIG_KEY = "signup";

export default function AdminConfig() {
  const { api } = useAdminAuth();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api(`/app-config/${CONFIG_KEY}`)
      .then(setCfg)
      .catch(() => setCfg(null))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) {
    return (
      <div className="p-8 text-white/70 text-sm">Caricamento configurazione…</div>
    );
  }
  if (!cfg) {
    return (
      <div className="p-8 text-rose-300 text-sm">
        Configurazione non trovata. Verrà creata al primo avvio del backend.
      </div>
    );
  }

  const update = (patch) => setCfg({ ...cfg, ...patch });
  const updatePolicy = (patch) =>
    setCfg({ ...cfg, password_policy: { ...cfg.password_policy, ...patch } });
  const updateMsg = (k, v) =>
    setCfg({ ...cfg, messages: { ...cfg.messages, [k]: v } });

  const toggleRequired = (field) => {
    const set = new Set(cfg.required_fields || []);
    if (set.has(field)) set.delete(field);
    else set.add(field);
    update({ required_fields: Array.from(set) });
  };

  const addGender = () =>
    update({
      genders: [
        ...(cfg.genders || []),
        { value: "NUOVO", label: "Nuovo" },
      ],
    });

  const updateGender = (i, patch) => {
    const next = [...(cfg.genders || [])];
    next[i] = { ...next[i], ...patch };
    update({ genders: next });
  };

  const removeGender = (i) => {
    const next = [...(cfg.genders || [])];
    next.splice(i, 1);
    update({ genders: next });
  };

  const addBlocked = () =>
    updatePolicy({
      blocked_common: [...(cfg.password_policy?.blocked_common || []), ""],
    });

  const updateBlocked = (i, v) => {
    const next = [...(cfg.password_policy?.blocked_common || [])];
    next[i] = v;
    updatePolicy({ blocked_common: next });
  };

  const removeBlocked = (i) => {
    const next = [...(cfg.password_policy?.blocked_common || [])];
    next.splice(i, 1);
    updatePolicy({ blocked_common: next });
  };

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      const res = await api(`/app-config/${CONFIG_KEY}`, {
        method: "PUT",
        data: cfg,
      });
      setCfg(res);
      setMsg("Salvato. Le modifiche sono attive immediatamente.");
    } catch (e) {
      setMsg(`Errore: ${e.message || "salvataggio fallito"}`);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 3500);
    }
  };

  const ALL_FIELDS = [
    "firstName",
    "lastName",
    "gender",
    "birthDate",
    "email",
    "password",
  ];

  return (
    <div className="p-8" data-testid="admin-config-page">
      <PageHeader
        title="Configurazione Signup"
        subtitle="Parametri del form di registrazione letti a caldo da MongoDB"
        actions={
          <Button onClick={save} disabled={saving} data-testid="config-save">
            <Save size={14} className="mr-1.5" />
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        }
      />

      {msg && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm ${
            msg.startsWith("Errore")
              ? "bg-rose-500/10 border border-rose-400/30 text-rose-200"
              : "bg-[#14c2a1]/10 border border-[#14c2a1]/30 text-[#67e8d1]"
          }`}
          data-testid="config-message"
        >
          {msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Età */}
        <Panel title="Range età">
          <div className="grid grid-cols-2 gap-3 p-4">
            <NumField
              label="Età minima"
              testid="config-min-age"
              value={cfg.min_age}
              onChange={(v) => update({ min_age: v })}
            />
            <NumField
              label="Età massima"
              testid="config-max-age"
              value={cfg.max_age}
              onChange={(v) => update({ max_age: v })}
            />
          </div>
        </Panel>

        {/* Campi obbligatori */}
        <Panel title="Campi obbligatori">
          <div className="flex flex-wrap gap-2 p-4">
            {ALL_FIELDS.map((f) => {
              const on = (cfg.required_fields || []).includes(f);
              return (
                <button
                  key={f}
                  type="button"
                  data-testid={`config-required-${f}`}
                  onClick={() => toggleRequired(f)}
                  className={`px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-all ${
                    on
                      ? "bg-[#14c2a1]/15 border-[#14c2a1]/40 text-[#67e8d1]"
                      : "bg-white/5 border-white/10 text-white/55 hover:text-white"
                  }`}
                >
                  {f}
                </button>
              );
            })}
          </div>
        </Panel>

        {/* Opzioni sesso */}
        <Panel
          title="Opzioni sesso"
          right={
            <Button
              variant="ghost"
              onClick={addGender}
              data-testid="config-add-gender"
            >
              <Plus size={14} className="mr-1" /> Aggiungi
            </Button>
          }
        >
          <div className="p-4 space-y-2">
            {(cfg.genders || []).map((g, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={g.value}
                  onChange={(e) => updateGender(i, { value: e.target.value })}
                  className="flex-1 h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
                  placeholder="VALUE"
                />
                <input
                  value={g.label}
                  onChange={(e) => updateGender(i, { label: e.target.value })}
                  className="flex-1 h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
                  placeholder="Etichetta"
                />
                <button
                  onClick={() => removeGender(i)}
                  className="h-9 w-9 rounded-lg bg-rose-500/10 border border-rose-400/20 text-rose-200 hover:bg-rose-500/20 flex items-center justify-center"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </Panel>

        {/* Password policy */}
        <Panel
          title="Password policy"
          right={
            <Badge color="sky">
              <FileLock2 size={11} className="mr-1" />
              {cfg.password_policy?.min_length || 0} caratteri min.
            </Badge>
          }
        >
          <div className="p-4 space-y-3">
            <NumField
              label="Lunghezza minima"
              testid="config-password-min"
              value={cfg.password_policy?.min_length}
              onChange={(v) => updatePolicy({ min_length: v })}
            />
            <div className="grid grid-cols-2 gap-2">
              <ToggleField
                label="Maiuscola"
                testid="config-require-upper"
                value={cfg.password_policy?.require_uppercase}
                onChange={(v) => updatePolicy({ require_uppercase: v })}
              />
              <ToggleField
                label="Minuscola"
                testid="config-require-lower"
                value={cfg.password_policy?.require_lowercase}
                onChange={(v) => updatePolicy({ require_lowercase: v })}
              />
              <ToggleField
                label="Numero"
                testid="config-require-digit"
                value={cfg.password_policy?.require_digit}
                onChange={(v) => updatePolicy({ require_digit: v })}
              />
              <ToggleField
                label="Simbolo"
                testid="config-require-symbol"
                value={cfg.password_policy?.require_symbol}
                onChange={(v) => updatePolicy({ require_symbol: v })}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/60 text-[11px] uppercase tracking-wider">
                  Password bloccate (troppo comuni)
                </div>
                <button
                  onClick={addBlocked}
                  className="text-[11px] text-[#67e8d1] hover:text-white"
                >
                  + Aggiungi
                </button>
              </div>
              <div className="space-y-1.5">
                {(cfg.password_policy?.blocked_common || []).map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      value={p}
                      onChange={(e) => updateBlocked(i, e.target.value)}
                      className="flex-1 h-8 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[12px] focus:outline-none focus:border-[#14c2a1]"
                    />
                    <button
                      onClick={() => removeBlocked(i)}
                      className="h-8 w-8 rounded-lg bg-rose-500/10 border border-rose-400/20 text-rose-200 hover:bg-rose-500/20 flex items-center justify-center"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Panel>

        {/* Messaggi */}
        <Panel title="Messaggi di errore (italiano)" className="lg:col-span-2">
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(cfg.messages || {}).map(([k, v]) => (
              <div key={k}>
                <div className="text-white/50 text-[10px] uppercase tracking-wider mb-1">
                  {k}
                </div>
                <input
                  value={v}
                  onChange={(e) => updateMsg(k, e.target.value)}
                  className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[12px] focus:outline-none focus:border-[#14c2a1]"
                />
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="mt-6">
        <EmailDiagnosticPanel api={api} />
      </div>

      <div className="mt-6 text-white/45 text-[11px] flex items-center gap-2">
        <Settings2 size={12} />
        Le modifiche sono salvate nella collezione <code>app_config</code> e
        applicate a caldo al prossimo signup (nessun restart necessario).
      </div>
    </div>
  );
}

function EmailDiagnosticPanel({ api }) {
  const [health, setHealth] = useState(null);
  const [loadingH, setLoadingH] = useState(false);
  const [testTo, setTestTo] = useState("magni.nicco@gmail.com");
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadHealth = async () => {
    setLoadingH(true);
    try {
      const r = await api("/email/health");
      setHealth(r);
    } catch (e) {
      setHealth({ error: e?.response?.data?.detail || "Errore" });
    } finally {
      setLoadingH(false);
    }
  };

  const sendTest = async () => {
    if (!testTo) return;
    setSending(true);
    setTestResult(null);
    try {
      const r = await api("/email/test", {
        method: "POST",
        data: { to: testTo },
      });
      setTestResult(r);
    } catch (e) {
      setTestResult(e?.response?.data?.detail || { error: String(e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <Panel
      title="Diagnostica Email (SMTP)"
      right={<Mail size={14} className="text-white/45" />}
    >
      <div className="p-4 space-y-4" data-testid="email-diag-panel">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={loadHealth}
            disabled={loadingH}
            data-testid="email-health-btn"
          >
            <Settings2 size={13} /> Verifica configurazione
          </Button>
          {health && (
            <Badge color={health.configured ? "teal" : "rose"}>
              {health.configured ? "Config OK" : "Config mancante"}
            </Badge>
          )}
        </div>
        {health && (
          <div className="rounded-lg bg-black/30 border border-white/10 p-3 text-[12px] text-white/80 font-mono overflow-x-auto">
            <pre className="whitespace-pre-wrap" data-testid="email-health-output">
{JSON.stringify(health, null, 2)}
            </pre>
          </div>
        )}

        <div className="border-t border-white/10 pt-3">
          <div className="text-white/65 text-[12px] mb-2">
            Invia email di test (usa il template "welcome"). L'esito è verboso:
            stage di errore, codice SMTP, blacklist Aruba ecc.
          </div>
          <div className="flex gap-2">
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="destinatario@example.com"
              data-testid="email-test-input"
              className="flex-1 h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
            />
            <Button
              onClick={sendTest}
              disabled={sending || !testTo}
              data-testid="email-test-send-btn"
            >
              <Send size={13} /> {sending ? "Invio…" : "Invia test"}
            </Button>
          </div>
        </div>

        {testResult && (
          <div
            className={`rounded-lg border p-3 text-[12px] font-mono overflow-x-auto ${
              testResult.ok
                ? "bg-[#14c2a1]/10 border-[#14c2a1]/30"
                : "bg-rose-500/10 border-rose-400/30"
            }`}
            data-testid="email-test-output"
          >
            <pre className="whitespace-pre-wrap text-white/90">
{JSON.stringify(testResult, null, 2)}
            </pre>
            {testResult.dnsbl_blocked && (
              <div className="mt-2 text-amber-300 text-[12px] font-sans">
                ⚠ Aruba ha rifiutato la connessione per DNSBL: l'IP outbound
                <code className="mx-1">{testResult.outbound_ip}</code>
                è in blacklist. Soluzioni: deploy su IP non blacklistato,
                usare un relay (es. Mailgun/Resend) con FROM=
                <code>{testResult.from_email}</code>, oppure contattare Aruba
                per delisting.
              </div>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-white/60 text-[11px] uppercase tracking-wider mb-1">
        {label}
      </div>
      <input
        type="number"
        data-testid={testid}
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
      />
    </div>
  );
}

function ToggleField({ label, value, onChange, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!value)}
      className={`h-9 px-3 rounded-lg text-[12px] font-medium border transition-all text-left flex items-center justify-between ${
        value
          ? "bg-[#14c2a1]/15 border-[#14c2a1]/40 text-[#67e8d1]"
          : "bg-white/5 border-white/10 text-white/55"
      }`}
    >
      {label}
      <span className="text-[10px]">{value ? "ON" : "OFF"}</span>
    </button>
  );
}
